"use client";

// Lightweight scroll-reveal (fade + slide-up) using IntersectionObserver + CSS —
// a ~0-dependency replacement for framer-motion's `initial/whileInView` entrance
// animations. Same visual behaviour (content starts hidden, animates in when it
// scrolls into view) but keeps framer-motion (~50KB) out of the initial bundle.
//
// SEO note: the content is always in the server-rendered HTML; the element only
// starts visually hidden (opacity:0) exactly like the old framer-motion `initial`
// did — so this is no worse for crawlers, and it reveals immediately for any
// element already in view on load (e.g. above-the-fold hero blocks).

import { useEffect, useRef, useState, type ReactNode, type CSSProperties } from "react";

type Props = {
    children: ReactNode;
    /** Wrapper tag (default div). */
    as?: "div" | "section" | "span" | "li" | "ul";
    /** Stagger delay in ms. */
    delay?: number;
    className?: string;
    /** Vertical travel in px (default 20). */
    y?: number;
    style?: CSSProperties;
};

export default function Reveal({ children, as: Tag = "div", delay = 0, className = "", y = 20, style }: Props) {
    const ref = useRef<HTMLElement | null>(null);
    const [shown, setShown] = useState(false);

    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        // No IO (old browser / SSR edge) → just show it.
        if (typeof IntersectionObserver === "undefined") {
            const fallback = window.setTimeout(() => setShown(true), 0);
            return () => window.clearTimeout(fallback);
        }
        const io = new IntersectionObserver(
            (entries) => {
                if (entries[0]?.isIntersecting) {
                    setShown(true);
                    io.disconnect();
                }
            },
            { rootMargin: "0px 0px -80px 0px", threshold: 0.1 }
        );
        io.observe(el);
        return () => io.disconnect();
    }, []);

    const mergedStyle: CSSProperties = {
        ...style,
        transitionDelay: delay ? `${delay}ms` : undefined,
        // exposed to CSS so each instance can set its own travel distance
        ["--reveal-y" as string]: `${y}px`,
    };

    return (
        // @ts-expect-error dynamic tag with ref is fine at runtime
        <Tag ref={ref} className={`reveal${shown ? " reveal-in" : ""}${className ? " " + className : ""}`} style={mergedStyle}>
            {children}
        </Tag>
    );
}
